import { Apple, ArrowUpRight, Building2, Dumbbell, HeartPulse, Send } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { ClientPlanScreen, ClientScreen, DietPlanScreen, EventsScreen } from "@/features/platform/components/app-screens";
import { PlanDesk, ScheduleDesk } from "@/features/platform/components/desks";
import { Laptop, Phone } from "@/features/platform/components/devices";
import { Accent, displayTitle, Eyebrow, Section } from "@/features/platform/components/landing-ui";
import { FloatChip, Stage } from "@/features/platform/components/stage";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

type Persona = {
  id: string;
  icon: LucideIcon;
  name: () => string;
  body: () => string;
  tags: () => string[];
};

const PERSONAS: Persona[] = [
  {
    id: "trainer",
    icon: Dumbbell,
    name: () => m.for_whom_trainer(),
    body: () => m.for_whom_trainer_body(),
    tags: () => [m.for_whom_tag_plans(), m.for_whom_tag_packs(), m.for_whom_tag_live()],
  },
  {
    id: "studio",
    icon: Building2,
    name: () => m.for_whom_studio(),
    body: () => m.for_whom_studio_body(),
    tags: () => [m.for_whom_tag_team(), m.for_whom_tag_classes(), m.for_whom_tag_one_schedule()],
  },
  {
    id: "dietician",
    icon: Apple,
    name: () => m.for_whom_dietician(),
    body: () => m.for_whom_dietician_body(),
    tags: () => [m.for_whom_tag_menus(), m.for_whom_tag_meal_log(), m.for_whom_tag_consults()],
  },
  {
    id: "physio",
    icon: HeartPulse,
    name: () => m.for_whom_physio(),
    body: () => m.for_whom_physio_body(),
    tags: () => [m.for_whom_tag_visits(), m.for_whom_tag_home(), m.for_whom_tag_recovery()],
  },
];

/** One kind of business as a card: its icon glowing, what it gets here and three things it uses. */
const PersonaCard = ({ persona, index }: { persona: Persona; index: number }) => (
  <a
    href={`#for-${persona.id}`}
    className="group dark relative isolate flex min-h-[360px] flex-col overflow-hidden rounded-[28px] bg-[oklch(0.13_0.006_240)] p-6 text-foreground ring-1 ring-foreground/[0.07] outline-none ring-inset focus-visible:ring-2 focus-visible:ring-ring"
  >
    <span
      className={cn(
        "absolute -top-20 -right-20 -z-10 size-56 rounded-full blur-3xl transition-opacity duration-500 group-hover:opacity-100",
        index % 2 ? "bg-primary/25 opacity-70" : "bg-primary/35 opacity-80",
      )}
    />
    <div className="flex items-start justify-between">
      <span className="grid size-14 place-items-center rounded-[18px] bg-primary text-primary-foreground shadow-[0_14px_40px_-10px_var(--primary)]">
        <persona.icon aria-hidden className="size-6" />
      </span>
      <span className="grid size-10 place-items-center rounded-full bg-white/10 text-white transition-transform duration-200 group-hover:rotate-45">
        <ArrowUpRight aria-hidden className="size-4" />
      </span>
    </div>
    <h2 className="mt-auto pt-10 text-[26px] leading-[1.05] font-medium tracking-[-0.035em]">{persona.name()}</h2>
    <p className="mt-2.5 text-[15px] leading-[1.5] text-pretty text-muted-foreground">{persona.body()}</p>
    <ul className="mt-5 flex flex-wrap gap-1.5">
      {persona.tags().map((tag) => (
        <li key={tag} className="rounded-full bg-white/[0.08] px-3 py-1.5 text-[12px] font-semibold ring-1 ring-white/10 ring-inset">
          {tag}
        </li>
      ))}
    </ul>
  </a>
);

/**
 * The words that open `/who-its-for`, inside the head card, and the four kinds of business as
 * cards under them, each a jump to the tile that shows its day in the app.
 */
export const ForWhomHero = () => (
  <div className="pt-14 md:pt-20">
    <div className="mx-auto max-w-[880px] px-1 text-center">
      <Eyebrow>{m.platform_nav_for_whom()}</Eyebrow>
      <h1 className={cn(displayTitle, "mt-6")}>
        {m.for_whom_title_lead()} <Accent>{m.for_whom_title_tail()}</Accent>
      </h1>
      <p className="mx-auto mt-6 max-w-[56ch] text-lg leading-[1.55] text-pretty text-muted-foreground">
        {m.for_whom_lede()}
      </p>
    </div>
    <div className="mt-12 grid gap-3 sm:grid-cols-2 md:mt-14 lg:grid-cols-4">
      {PERSONAS.map((persona, index) => (
        <PersonaCard key={persona.id} persona={persona} index={index} />
      ))}
    </div>
  </div>
);

const laptopArt = "flex items-center justify-center px-5 pt-2 pb-12 lg:py-16";
const phoneArt = "flex justify-center px-6 pb-10 lg:items-center lg:py-14";

/** Each kind of business on the screen it lives in, the pictures zigzagging left and right. */
export const ForWhomDays = () => (
  <Section>
    <div className="mb-10 max-w-[780px] px-1">
      <Eyebrow>{m.for_whom_days_eyebrow()}</Eyebrow>
      <h2 className={cn(displayTitle, "mt-5")}>
        {m.for_whom_days_lead()} <Accent>{m.for_whom_days_tail()}</Accent>
      </h2>
    </div>
    <div className="grid gap-3">
      <Stage
        id="for-trainer"
        side
        eyebrow={m.for_whom_trainer()}
        title={m.for_whom_trainer_title()}
        lead={m.features_plan_lead()}
        body={m.for_whom_trainer_long()}
        className="lg:min-h-[620px]"
        artClassName={cn(laptopArt, "lg:pr-12 lg:pl-0")}
      >
        <div className="relative w-full max-w-[800px] pr-[10%]">
          <Laptop>
            <PlanDesk />
          </Laptop>
          <Phone className="absolute right-0 -bottom-[7%] w-[20%]">
            <ClientPlanScreen />
          </Phone>
          <FloatChip className="bottom-[16%] -left-[3%] max-lg:hidden">
            <Send className="size-4 text-primary" />
            {m.features_plan_chip({ name: m.features_sample_client_first() })}
          </FloatChip>
        </div>
      </Stage>
      <Stage
        id="for-studio"
        side
        flip
        eyebrow={m.for_whom_studio()}
        title={m.for_whom_studio_title()}
        lead={m.for_whom_studio_lead()}
        body={m.for_whom_studio_long()}
        className="lg:min-h-[620px]"
        artClassName={cn(laptopArt, "lg:pr-0 lg:pl-12")}
      >
        <div className="relative w-full max-w-[800px] pl-[10%]">
          <Laptop>
            <ScheduleDesk />
          </Laptop>
          <Phone className="absolute left-0 -bottom-[7%] w-[20%]">
            <EventsScreen />
          </Phone>
        </div>
      </Stage>
      <Stage
        id="for-dietician"
        side
        eyebrow={m.for_whom_dietician()}
        title={m.for_whom_dietician_title()}
        lead={m.for_whom_dietician_lead()}
        body={m.for_whom_dietician_long()}
        className="lg:min-h-[620px]"
        artClassName={phoneArt}
      >
        <div className="w-[min(290px,78%)] lg:w-[310px]">
          <Phone>
            <DietPlanScreen />
          </Phone>
        </div>
      </Stage>
      <Stage
        id="for-physio"
        side
        flip
        eyebrow={m.for_whom_physio()}
        title={m.for_whom_physio_title()}
        lead={m.for_whom_physio_lead()}
        body={m.for_whom_physio_long()}
        className="lg:min-h-[620px]"
        artClassName={phoneArt}
      >
        <div className="w-[min(290px,78%)] lg:w-[310px]">
          <Phone>
            <ClientScreen />
          </Phone>
        </div>
      </Stage>
    </div>
  </Section>
);
